import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { fabric } from "fabric";
import {
  Printer, Type, Square, Circle as CircleIcon, ImagePlus, Sparkles, Trash2,
  ArrowUp, ArrowDown, Copy, Loader2, Send, Save, FolderOpen, ArrowLeft, LayoutTemplate, IndianRupee,
} from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const PRESETS = [
  { key: "flex", label: "Flex Banner 6×3 ft", w: 720, h: 360, size: "6 ft × 3 ft" },
  { key: "standee", label: "Roll-up Standee 3×6 ft", w: 300, h: 600, size: "3 ft × 6 ft" },
  { key: "a3", label: "A3 Poster", w: 400, h: 566, size: "A3 (297×420 mm)" },
  { key: "led", label: "LED Name Board 8×2 ft", w: 760, h: 190, size: "8 ft × 2 ft" },
  { key: "square", label: "Social Post (Square)", w: 500, h: 500, size: "1080×1080 px" },
  { key: "custom", label: "Custom Canvas", w: 700, h: 450, size: "Custom" },
];

const FONTS = ["Outfit", "DM Sans", "Georgia", "Impact", "Courier New", "Arial"];

const loadRazorpay = () => new Promise((res) => {
  if (window.Razorpay) return res(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => res(true);
  s.onerror = () => res(false);
  document.body.appendChild(s);
});

const QUOTE_BADGE = {
  submitted: { label: "Awaiting price", cls: "bg-amber-100 text-amber-700" },
  quoted: { label: "Price ready", cls: "bg-blue-100 text-blue-700" },
  paid: { label: "Paid", cls: "bg-green-100 text-green-700" },
};

export default function DesignStudio() {
  const canvasElRef = useRef(null);
  const fabricRef = useRef(null);
  const fileRef = useRef(null);
  const [sel, setSel] = useState(null);
  const [preset, setPreset] = useState("flex");
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [details, setDetails] = useState({ name: "", phone: "", notes: "" });
  const [designId, setDesignId] = useState(localStorage.getItem("cp_design_id") || null);
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [payingId, setPayingId] = useState(null);

  const currentPreset = PRESETS.find((p) => p.key === preset) || PRESETS[0];

  useEffect(() => {
    const c = new fabric.Canvas(canvasElRef.current, { backgroundColor: "#ffffff", preserveObjectStacking: true });
    c.setDimensions({ width: PRESETS[0].w, height: PRESETS[0].h });
    fabricRef.current = c;
    const update = () => setSel(c.getActiveObject() || null);
    c.on("selection:created", update);
    c.on("selection:updated", update);
    c.on("selection:cleared", () => setSel(null));
    return () => c.dispose();
  }, []);

  const applyPreset = (key) => {
    const p = PRESETS.find((x) => x.key === key);
    setPreset(key);
    fabricRef.current?.setDimensions({ width: p.w, height: p.h });
    fabricRef.current?.renderAll();
  };

  const addText = (heading) => {
    const c = fabricRef.current;
    const t = new fabric.Textbox(heading ? "Your Heading" : "Add your text here", {
      left: 40, top: 40, fontSize: heading ? 44 : 24, fill: "#0f172a",
      fontFamily: "Outfit", fontWeight: heading ? "700" : "400", width: c.getWidth() * 0.7,
    });
    c.add(t); c.setActiveObject(t); c.renderAll();
  };

  const addShape = (kind) => {
    const c = fabricRef.current;
    const shape = kind === "rect"
      ? new fabric.Rect({ left: 60, top: 60, width: 160, height: 100, fill: "#D97706", rx: 8, ry: 8 })
      : new fabric.Circle({ left: 60, top: 60, radius: 60, fill: "#0f172a" });
    c.add(shape); c.setActiveObject(shape); c.renderAll();
  };

  const addImageFromUrl = (url, scaleFactor = 0.5) => {
    const c = fabricRef.current;
    fabric.Image.fromURL(url, (img) => {
      img.scaleToWidth(c.getWidth() * scaleFactor);
      img.set({ left: 40, top: 40 });
      c.add(img); c.setActiveObject(img); c.renderAll();
    });
  };

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => addImageFromUrl(ev.target.result, 0.4);
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const genAi = async () => {
    if (!aiPrompt.trim()) { toast.error("Describe the image first"); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/design/ai-image", { prompt: aiPrompt });
      addImageFromUrl(`${API_BASE}/content/image/${data.id}`, 0.6);
      toast.success("AI image added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally { setAiLoading(false); }
  };

  const loadTemplate = (kind) => {
    const c = fabricRef.current;
    c.clear(); c.backgroundColor = "#ffffff";
    if (kind === "sale") {
      c.backgroundColor = "#0f172a";
      c.add(new fabric.Textbox("MEGA SALE", { left: 40, top: 30, width: 640, fontSize: 56, fill: "#F59E0B", fontFamily: "Impact", fontWeight: "700" }));
      c.add(new fabric.Textbox("Flex Banners · 50% OFF", { left: 40, top: 150, width: 640, fontSize: 26, fill: "#ffffff", fontFamily: "Outfit" }));
      c.add(new fabric.Textbox("Call +91 63630 32874", { left: 40, top: 230, width: 640, fontSize: 22, fill: "#F59E0B", fontFamily: "DM Sans" }));
    } else if (kind === "opening") {
      c.add(new fabric.Rect({ left: 0, top: 0, width: c.getWidth(), height: 70, fill: "#D97706" }));
      c.add(new fabric.Textbox("GRAND OPENING", { left: 30, top: 18, fontSize: 34, fill: "#ffffff", fontFamily: "Outfit", fontWeight: "700" }));
      c.add(new fabric.Textbox("Your Shop Name Here", { left: 40, top: 120, fontSize: 40, fill: "#0f172a", fontFamily: "Georgia" }));
      c.add(new fabric.Textbox("Address · Date · Time", { left: 40, top: 180, fontSize: 22, fill: "#475569", fontFamily: "DM Sans" }));
    } else {
      c.add(new fabric.Textbox("SHOP NAME", { left: 40, top: 50, fontSize: 54, fill: "#0f172a", fontFamily: "Impact", fontWeight: "700" }));
      c.add(new fabric.Textbox("Tagline goes here", { left: 42, top: 120, fontSize: 24, fill: "#D97706", fontFamily: "Outfit" }));
    }
    c.renderAll();
  };

  const setFill = (color) => { const o = fabricRef.current.getActiveObject(); if (o) { o.set("fill", color); fabricRef.current.renderAll(); setSel({ ...o }); } };
  const setBg = (color) => { fabricRef.current.backgroundColor = color; fabricRef.current.renderAll(); };
  const setProp = (k, v) => { const o = fabricRef.current.getActiveObject(); if (o) { o.set(k, v); fabricRef.current.renderAll(); setSel({ ...o, [k]: v }); } };
  const del = () => { const o = fabricRef.current.getActiveObject(); if (o) { fabricRef.current.remove(o); fabricRef.current.discardActiveObject(); fabricRef.current.renderAll(); setSel(null); } };
  const layer = (dir) => { const o = fabricRef.current.getActiveObject(); if (!o) return; dir > 0 ? fabricRef.current.bringToFront(o) : fabricRef.current.sendToBack(o); fabricRef.current.renderAll(); };
  const duplicate = () => { const o = fabricRef.current.getActiveObject(); if (!o) return; o.clone((cl) => { cl.set({ left: (o.left || 0) + 20, top: (o.top || 0) + 20 }); fabricRef.current.add(cl); fabricRef.current.setActiveObject(cl); fabricRef.current.renderAll(); }); };

  const saveDraft = useCallback(async () => {
    if (!details.phone.trim()) { toast.error("Enter your phone to save a draft"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/designs", {
        id: designId, name: details.name, phone: details.phone,
        product: currentPreset.label, print_size: currentPreset.size,
        width: currentPreset.w, height: currentPreset.h, json: fabricRef.current.toJSON(),
      });
      setDesignId(data.id); localStorage.setItem("cp_design_id", data.id);
      toast.success("Draft saved — you can resume later with your phone number");
    } catch { toast.error("Could not save draft"); }
    finally { setBusy(false); }
  }, [designId, details, currentPreset]);

  const loadDrafts = async (phoneArg) => {
    const phone = (typeof phoneArg === "string" ? phoneArg : details.phone).trim();
    if (!phone) { toast.error("Enter your phone to find drafts"); return; }
    try {
      const { data } = await api.get("/designs", { params: { phone } });
      setDrafts(data);
      if (!data.length) toast.info("No saved designs for this number");
    } catch { toast.error("Could not load drafts"); }
  };

  const openDraft = async (id) => {
    try {
      const { data } = await api.get(`/designs/${id}`, { params: { phone: details.phone.trim() } });
      const p = PRESETS.find((x) => x.label === data.product) || PRESETS[0];
      applyPreset(p.key);
      fabricRef.current.loadFromJSON(data.json, () => fabricRef.current.renderAll());
      setDesignId(id); localStorage.setItem("cp_design_id", id);
      setDrafts([]);
      toast.success("Draft loaded");
    } catch { toast.error("Could not open draft"); }
  };

  const sendForPrinting = async () => {
    if (!details.name.trim() || !details.phone.trim()) { toast.error("Please enter your name and phone"); return; }
    setBusy(true);
    try {
      const dataUrl = fabricRef.current.toDataURL({ format: "png", multiplier: 2 });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "design.png", { type: "image/png" });
      const fd = new FormData(); fd.append("file", file);
      const { data: fileRec } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data: d } = await api.post("/designs", {
        id: designId, name: details.name, phone: details.phone,
        product: currentPreset.label, print_size: currentPreset.size,
        width: currentPreset.w, height: currentPreset.h, json: fabricRef.current.toJSON(),
        submit: true, preview_path: fileRec.storage_path, notes: details.notes,
      });
      setDesignId(d.id); localStorage.setItem("cp_design_id", d.id);
      await api.post("/leads", {
        name: details.name, phone: details.phone, service: `Design Studio — ${currentPreset.label}`,
        dimensions: currentPreset.size, source: "design-studio",
        message: `Custom design created in Design Studio. ${details.notes || ""}\nDesign ref: ${d.id}`.trim(),
        files: [fileRec],
      });
      toast.success("Design sent! Our team will review and send you a price. Come back here and tap “Find my saved drafts” to pay once it's quoted.");
      loadDrafts(details.phone);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send design");
    } finally { setBusy(false); }
  };

  const payQuote = async (d) => {
    setPayingId(d.id);
    try {
      const { data } = await api.post("/payments/create-design-order", { design_id: d.id, phone: details.phone.trim() });
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load payment window"); setPayingId(null); return; }
      const rzp = new window.Razorpay({
        key: data.key_id, order_id: data.razorpay_order_id, amount: data.amount * 100, currency: data.currency,
        name: "Creative Prints", description: `Custom design — ${d.product}`,
        prefill: { name: details.name, contact: details.phone },
        theme: { color: "#D97706" },
        handler: async (resp) => {
          try {
            await api.post("/payments/verify", { ...resp, our_order_id: data.our_order_id });
            toast.success("Payment successful! We'll start printing your design.");
            loadDrafts(details.phone);
          } catch { toast.error("Payment verification failed — contact us if money was deducted"); }
          setPayingId(null);
        },
        modal: { ondismiss: () => setPayingId(null) },
      });
      rzp.on("payment.failed", () => { toast.error("Payment failed"); setPayingId(null); });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start payment");
      setPayingId(null);
    }
  };

  const isText = sel && (sel.type === "textbox" || sel.type === "i-text" || sel.type === "text");

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-[1400px] px-4 flex h-14 items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><span className="grid h-8 w-8 place-items-center rounded-md bg-primary text-white"><Printer size={18} /></span><span className="font-heading font-extrabold text-slate-900">Design Studio</span></Link>
          <div className="flex items-center gap-2">
            <select data-testid="design-preset" value={preset} onChange={(e) => applyPreset(e.target.value)} className="rounded-md border border-input bg-white px-3 py-1.5 text-sm font-medium">
              {PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
            <Button data-testid="design-save" variant="outline" size="sm" className="gap-2" onClick={saveDraft} disabled={busy}><Save size={15} /> <span className="hidden sm:inline">Save</span></Button>
            <Button data-testid="design-send" size="sm" className="gap-2 bg-primary hover:bg-amber-700 text-white" onClick={sendForPrinting} disabled={busy}>{busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send for Printing</Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-4 py-5 grid grid-cols-1 lg:grid-cols-[220px_1fr_260px] gap-4">
        {/* Tools */}
        <aside className="rounded-xl border border-slate-200 bg-white p-3 space-y-2 h-fit">
          <p className="px-1 text-xs font-bold uppercase tracking-wide text-slate-400">Add</p>
          <div className="grid grid-cols-2 gap-2">
            <ToolBtn testid="tool-text" icon={Type} label="Text" onClick={() => addText(false)} />
            <ToolBtn testid="tool-heading" icon={Type} label="Heading" onClick={() => addText(true)} />
            <ToolBtn testid="tool-rect" icon={Square} label="Rect" onClick={() => addShape("rect")} />
            <ToolBtn testid="tool-circle" icon={CircleIcon} label="Circle" onClick={() => addShape("circle")} />
            <ToolBtn testid="tool-upload" icon={ImagePlus} label="Upload" onClick={() => fileRef.current?.click()} />
            <ToolBtn testid="tool-ai" icon={Sparkles} label="AI Image" onClick={() => document.getElementById("ai-box")?.scrollIntoView()} />
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="design-upload-input" />

          <p className="px-1 pt-2 text-xs font-bold uppercase tracking-wide text-slate-400 flex items-center gap-1"><LayoutTemplate size={12} /> Templates</p>
          <div className="space-y-2">
            <button data-testid="tpl-sale" onClick={() => loadTemplate("sale")} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:border-primary hover:text-primary text-left">Mega Sale banner</button>
            <button data-testid="tpl-opening" onClick={() => loadTemplate("opening")} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:border-primary hover:text-primary text-left">Grand Opening</button>
            <button data-testid="tpl-name" onClick={() => loadTemplate("name")} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:border-primary hover:text-primary text-left">Name Board</button>
          </div>

          <div id="ai-box" className="pt-2">
            <p className="px-1 text-xs font-bold uppercase tracking-wide text-slate-400 flex items-center gap-1"><Sparkles size={12} /> AI Image</p>
            <Textarea data-testid="design-ai-prompt" rows={3} value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} placeholder="e.g. festive marigold background" className="mt-1 text-sm" />
            <Button data-testid="design-ai-gen" size="sm" className="mt-2 w-full gap-2 bg-slate-900 hover:bg-slate-800 text-white" onClick={genAi} disabled={aiLoading}>{aiLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Generate</Button>
          </div>

          <div className="pt-2">
            <Label className="px-1 text-xs">Canvas background</Label>
            <input data-testid="design-bg" type="color" defaultValue="#ffffff" onChange={(e) => setBg(e.target.value)} className="mt-1 h-9 w-full rounded-md border border-slate-200" />
          </div>
        </aside>

        {/* Canvas */}
        <main className="rounded-xl border border-slate-200 bg-slate-50 p-4 grid place-items-center overflow-auto">
          <div className="shadow-xl bg-white"><canvas ref={canvasElRef} data-testid="design-canvas" /></div>
        </main>

        {/* Right panel */}
        <aside className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Selected element</p>
            {sel ? (
              <div className="mt-3 space-y-3">
                <div>
                  <Label className="text-xs">Color</Label>
                  <input data-testid="sel-color" type="color" value={sel.fill || "#000000"} onChange={(e) => setFill(e.target.value)} className="mt-1 h-9 w-full rounded-md border border-slate-200" />
                </div>
                {isText && (
                  <>
                    <div>
                      <Label className="text-xs">Font</Label>
                      <select value={sel.fontFamily || "Outfit"} onChange={(e) => setProp("fontFamily", e.target.value)} className="mt-1 w-full rounded-md border border-input px-2 py-1.5 text-sm">
                        {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Font size: {Math.round(sel.fontSize || 24)}</Label>
                      <input type="range" min="10" max="120" value={sel.fontSize || 24} onChange={(e) => setProp("fontSize", parseInt(e.target.value))} className="mt-1 w-full" />
                    </div>
                    <button onClick={() => setProp("fontWeight", sel.fontWeight === "700" ? "400" : "700")} className="rounded-md border border-slate-200 px-3 py-1.5 text-sm font-bold w-full">Toggle Bold</button>
                  </>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" size="sm" className="gap-1" onClick={() => layer(1)}><ArrowUp size={14} /> Front</Button>
                  <Button variant="outline" size="sm" className="gap-1" onClick={() => layer(-1)}><ArrowDown size={14} /> Back</Button>
                  <Button variant="outline" size="sm" className="gap-1" onClick={duplicate}><Copy size={14} /> Copy</Button>
                  <Button data-testid="sel-delete" variant="outline" size="sm" className="gap-1 text-red-600" onClick={del}><Trash2 size={14} /> Delete</Button>
                </div>
              </div>
            ) : (
              <p className="mt-2 text-sm text-slate-400">Click an element on the canvas to edit it.</p>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Your details</p>
            <div className="mt-3 space-y-2">
              <Input data-testid="design-name" placeholder="Your name" value={details.name} onChange={(e) => setDetails((d) => ({ ...d, name: e.target.value }))} />
              <Input data-testid="design-phone" placeholder="Phone / WhatsApp" value={details.phone} onChange={(e) => setDetails((d) => ({ ...d, phone: e.target.value }))} />
              <Textarea data-testid="design-notes" rows={2} placeholder="Notes (quantity, finish, deadline)" value={details.notes} onChange={(e) => setDetails((d) => ({ ...d, notes: e.target.value }))} />
              <Button variant="outline" size="sm" className="w-full gap-2" onClick={() => loadDrafts()} data-testid="design-load-drafts"><FolderOpen size={14} /> Find my saved designs</Button>
              {drafts.length > 0 && (
                <div className="space-y-2">
                  {drafts.map((d) => {
                    const badge = QUOTE_BADGE[d.quote_status];
                    return (
                      <div key={d.id} data-testid={`draft-${d.id}`} className="rounded-md border border-slate-200 p-2 text-xs">
                        <div className="flex items-center justify-between gap-2">
                          <button onClick={() => openDraft(d.id)} className="text-left font-medium text-slate-700 hover:text-primary" data-testid={`draft-open-${d.id}`}>
                            {d.product} · {new Date(d.updated_at).toLocaleDateString()}
                          </button>
                          {badge && <span className={`shrink-0 rounded-full px-2 py-0.5 font-bold ${badge.cls}`}>{badge.label}</span>}
                        </div>
                        {d.quote_status === "quoted" && (
                          <div className="mt-2">
                            {d.quote_note && <p className="mb-1.5 text-slate-500">{d.quote_note}</p>}
                            <Button data-testid={`draft-pay-${d.id}`} size="sm" className="w-full gap-1.5 bg-primary hover:bg-amber-700 text-white" onClick={() => payQuote(d)} disabled={payingId === d.id}>
                              {payingId === d.id ? <Loader2 size={14} className="animate-spin" /> : <IndianRupee size={14} />} Pay ₹{d.quote_amount}
                            </Button>
                          </div>
                        )}
                        {d.quote_status === "submitted" && <p className="mt-1 text-slate-400">Our team is preparing your price.</p>}
                        {d.quote_status === "paid" && <p className="mt-1 text-green-600 font-medium">Paid — in production 🎉</p>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <Link to="/" className="flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-primary"><ArrowLeft size={14} /> Back to website</Link>
        </aside>
      </div>
    </div>
  );
}

const ToolBtn = ({ icon: Icon, label, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick} className="flex flex-col items-center gap-1 rounded-md border border-slate-200 py-2.5 text-xs font-semibold text-slate-700 hover:border-primary hover:text-primary transition-colors">
    <Icon size={18} /> {label}
  </button>
);
